import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { backendApi } from "@/services/backend/api";
import {
  CreateGfsOfferFromCalculationRequest,
  CreateGfsOfferFromOldRequest,
  CreateGfsOfferRequest,
  ClearGfsOfferSignatureRequest,
  DeleteGfsOfferRequest,
  GfsOffer,
  GfsOfferSignature,
  GfsOfferConfig,
  GfsOfferConfigRequest,
  GfsOfferList,
  GfsOfferPdfRequest,
  ListGfsOffersRequest,
  LoadGfsOfferCalculationRequest,
  PutGfsOfferRequest,
  PutGfsOfferConfigRequest,
  SetGfsOfferDateRequest,
  SetGfsOfferImmutableRequest,
  SetGfsOfferValidUntilDateRequest,
  SignGfsOfferRequest,
  UpdateGfsOfferObjectSelectionRequest,
  UseGfsCustomerPostalAddressRequest,
} from "@/services/backend/gfs/offer/types";

const gfsOfferApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "GfsOffer",
      "GfsOffers",
      "GfsOfferConfig",
      "GfsOfferSignature",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      gfsOfferCreate: build.mutation<GfsOffer, CreateGfsOfferRequest>({
        query: (body) => ({ url: "/gfs/offers", method: "POST", body }),
        invalidatesTags: [{ type: "GfsOffers", id: "LIST" }],
      }),
      gfsOfferCreateFromCalculation: build.mutation<
        GfsOffer,
        CreateGfsOfferFromCalculationRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/create-from-calculation",
          method: "POST",
          body,
        }),
        invalidatesTags: [{ type: "GfsOffers", id: "LIST" }],
      }),
      gfsOfferCreateFromOld: build.mutation<
        GfsOffer,
        CreateGfsOfferFromOldRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/create-from-old",
          method: "POST",
          body,
        }),
        invalidatesTags: [{ type: "GfsOffers", id: "LIST" }],
      }),
      gfsOfferLoadCalculation: build.mutation<
        GfsOffer,
        LoadGfsOfferCalculationRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/load-calculation",
          method: "POST",
          body,
        }),
        async onQueryStarted(request, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              gfsOfferApi.util.updateQueryData(
                "gfsOfferGet",
                { id: request.id },
                (draft) => Object.assign(draft, data),
              ),
            );
          } catch {
            return;
          }
        },
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.id },
          { type: "GfsOffers", id: "LIST" },
          { type: "NumbersDocumentNumber", id: request.id },
        ],
      }),
      gfsOfferPut: build.mutation<GfsOffer, PutGfsOfferRequest>({
        query: (body) => ({ url: "/gfs/offers", method: "PUT", body }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.id },
          { type: "GfsOffers", id: "LIST" },
        ],
      }),
      gfsOfferSetDate: build.mutation<GfsOffer, SetGfsOfferDateRequest>({
        query: (body) => ({
          url: "/gfs/offers/set-date",
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.offerId },
          { type: "GfsOffers", id: "LIST" },
        ],
      }),
      gfsOfferSetImmutable: build.mutation<
        GfsOffer,
        SetGfsOfferImmutableRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/set-immutable",
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.id },
          { type: "GfsOffers", id: "LIST" },
        ],
      }),
      gfsOfferUseCustomerPostalAddress: build.mutation<
        GfsOffer,
        UseGfsCustomerPostalAddressRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/postal-address/from-customer",
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.offerId },
        ],
      }),
      gfsOfferUpdateObjectSelection: build.mutation<
        GfsOffer,
        UpdateGfsOfferObjectSelectionRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/object-selection",
          method: "PUT",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.offerId },
          { type: "GfsOffers", id: "LIST" },
        ],
      }),
      gfsOfferSetValidUntilDate: build.mutation<
        GfsOffer,
        SetGfsOfferValidUntilDateRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/set-valid-until-date",
          method: "POST",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.offerId },
          { type: "GfsOffers", id: "LIST" },
        ],
      }),
      gfsOfferSign: build.mutation<GfsOffer, SignGfsOfferRequest>({
        query: (request) => {
          const body = new FormData();
          body.append("file", request.signature);
          return {
            url: "/gfs/offers/sign",
            method: "PATCH",
            headers: { Accept: "application/json" },
            params: {
              id: request.id,
              dataFormat: request.dataFormat,
              nameOfSigner: request.nameOfSigner,
              signatureLocation: request.signatureLocation,
              signatureDate: request.signatureDate,
            },
            body,
            formData: true,
          };
        },
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.id },
          { type: "GfsOffers", id: "LIST" },
          { type: "GfsOfferSignature", id: request.id },
        ],
      }),
      gfsOfferClearSignature: build.mutation<
        GfsOffer,
        ClearGfsOfferSignatureRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/signature",
          method: "DELETE",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.offerId },
          { type: "GfsOffers", id: "LIST" },
          { type: "GfsOfferSignature", id: request.offerId },
        ],
      }),
      gfsOfferDelete: build.mutation<void, DeleteGfsOfferRequest>({
        query: (body) => ({ url: "/gfs/offers", method: "DELETE", body }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.id },
          { type: "GfsOffers", id: "LIST" },
        ],
      }),
      gfsOfferGet: build.query<GfsOffer, { id: string }>({
        query: (params) => ({
          url: "/gfs/offers/get",
          method: "GET",
          params,
        }),
        providesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.id },
        ],
      }),
      gfsOfferSignature: build.query<GfsOfferSignature, { id: string }>({
        query: (request) => ({
          url: "/gfs/offers/signature",
          method: "GET",
          params: request,
        }),
        providesTags: (_result, _error, request) => [
          { type: "GfsOfferSignature", id: request.id },
        ],
      }),
      gfsOfferConfigGet: build.query<GfsOfferConfig, GfsOfferConfigRequest>({
        query: (request) => ({
          url: "/gfs/offer-config",
          method: "GET",
          params: request,
        }),
        providesTags: [{ type: "GfsOfferConfig", id: "GLOBAL" }],
      }),
      gfsOfferConfigPut: build.mutation<
        GfsOfferConfig,
        PutGfsOfferConfigRequest
      >({
        query: (body) => ({ url: "/gfs/offer-config", method: "PUT", body }),
        async onQueryStarted(request, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              gfsOfferApi.util.updateQueryData(
                "gfsOfferConfigGet",
                { companyId: request.companyId },
                () => data,
              ),
            );
          } catch {
            return;
          }
        },
        invalidatesTags: [{ type: "GfsOfferConfig", id: "GLOBAL" }],
      }),
      gfsOfferConfigPreviewPdf: build.query<
        { objectURL: string },
        GfsOfferConfigRequest
      >({
        query: (request) => ({
          url: "/gfs/offer-config/pdf",
          method: "GET",
          params: request,
          headers: { Accept: "application/pdf" },
          responseHandler: previewResponseHandler,
          cache: "no-cache",
        }),
      }),
      gfsOfferList: build.query<GfsOfferList, ListGfsOffersRequest>({
        query: (params) => ({
          url: "/gfs/offers/list",
          method: "GET",
          params: base64ListRequest(params),
        }),
        providesTags: [{ type: "GfsOffers", id: "LIST" }],
      }),
      gfsOfferPdfPreview: build.query<
        { objectURL: string },
        GfsOfferPdfRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/pdf",
          method: "PATCH",
          body,
          headers: { Accept: "application/pdf" },
          responseHandler: previewResponseHandler,
          cache: "no-cache",
        }),
        providesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.offerId },
        ],
      }),
      gfsOfferPdfDownload: build.mutation<
        { filename: string; objectURL: string },
        GfsOfferPdfRequest
      >({
        query: (body) => ({
          url: "/gfs/offers/pdf",
          method: "PATCH",
          body,
          headers: { Accept: "application/pdf" },
          responseHandler: downloadResponseHandler,
          cache: "no-cache",
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "GfsOffer", id: request.offerId },
        ],
      }),
    }),
  });

export const {
  useGfsOfferCreateMutation,
  useGfsOfferCreateFromCalculationMutation,
  useGfsOfferCreateFromOldMutation,
  useGfsOfferLoadCalculationMutation,
  useGfsOfferPutMutation,
  useGfsOfferSetDateMutation,
  useGfsOfferSetImmutableMutation,
  useGfsOfferUseCustomerPostalAddressMutation,
  useGfsOfferUpdateObjectSelectionMutation,
  useGfsOfferSetValidUntilDateMutation,
  useGfsOfferSignMutation,
  useGfsOfferClearSignatureMutation,
  useGfsOfferDeleteMutation,
  useGfsOfferGetQuery,
  useGfsOfferSignatureQuery,
  useGfsOfferConfigGetQuery,
  useGfsOfferConfigPutMutation,
  useLazyGfsOfferConfigPreviewPdfQuery,
  useGfsOfferListQuery,
  useGfsOfferPdfPreviewQuery,
  useGfsOfferPdfDownloadMutation,
} = gfsOfferApi;

export function updateGfsOfferConfigCache(
  companyId: string,
  config: GfsOfferConfig,
) {
  return gfsOfferApi.util.updateQueryData(
    "gfsOfferConfigGet",
    { companyId },
    () => config,
  );
}

/**
 * The offer editor updates the cached offer immediately and debounces the full
 * PUT request. Keeping this cache update next to the endpoint prevents local
 * editor state from racing the query cache after a successful save.
 */
export function updateGfsOfferCache(offer: GfsOffer) {
  return gfsOfferApi.util.updateQueryData(
    "gfsOfferGet",
    { id: offer.id },
    (draft) => Object.assign(draft, offer),
  );
}
